import { useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UAParser } from 'ua-parser-js';
import { v4 as uuidv4 } from 'uuid';
import { useLocation } from 'react-router-dom';
import ym from 'react-yandex-metrika';

import { getIsLoggedIn, getUserInfo } from '@store/auth/auth-selectors';
import { AppDispatch } from '@store/store';
import {
  getFirstTimePages,
  // getGeoLocation,
  getGuestId,
  getSessionId,
} from '@store/metric/metric-selectors';
import { markPageAsVisited, setGuestId, setSessionId } from '@store/metric/metric-slice';
import { initMetrics, sendAnalyticsEvent, touchSession } from '@store/metric/metric-operations';
// import { fetchUserGeoLocation } from '@store/metric/metric-geo-operations';
import { getPageSlug } from '@configs/metrics/ROUTE_SLUGS';
import { LEGACY_EVENT_ALIASES, METRIC_EVENTS, MetricEventName } from '@configs/metrics/events';
import { BackendAnalyticsEvent } from '@store/metric/MetricTypes';
import { getCookie, setCookie } from '@utils/cookies';
import { getCurrentPageUtm, mergeUtmParams, readUtmParamsFromSearch } from '@utils/utmTracking';

interface MetricParams {
  insert_id: string;
  user_id?: string | null;
  guest_id?: string | null;
  is_first_time_on_page: boolean;
  source: string;
  trigger_source: string;
  utm_source?: string;
  utm_content?: string;
  event_date: string;
  session_id: string | null;
  platform?: string;
  os_name?: string;
  device_name?: string;
  [key: string]: string | number | boolean | null | undefined;
}

const useMetrics = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const currentPage = useMemo(() => getPageSlug(location.pathname), [location.pathname]);

  const user = useSelector(getUserInfo);
  const isLoggedIn = useSelector(getIsLoggedIn);

  const guestId = useSelector(getGuestId);
  const firstTimePages = useSelector(getFirstTimePages);
  // const geoLocation = useSelector(getGeoLocation);
  const sessionId = useSelector(getSessionId);

  const uaResult = useMemo(() => new UAParser(navigator.userAgent).getResult(), []);

  // // helpers
  // const fetchGeoLocation = useCallback(() => {
  //   if (geoLocation.region === 'unknown' && geoLocation.city === 'unknown') {
  //     dispatch(fetchUserGeoLocation());
  //   }
  // }, [dispatch, geoLocation.city, geoLocation.region]);

  const getPlatformByWidth = () => {
    const MOBILE_MD = parseInt(String('720'), 10);
    const DESKTOP = parseInt(String('1920'), 10);
    const w =
      typeof window !== 'undefined'
        ? window.innerWidth || document.documentElement.clientWidth
        : DESKTOP;

    if (w < MOBILE_MD) return 'mobile';
    if (w < DESKTOP) return 'tablet';
    return 'desktop';
  };

  const fetchSessionId = useCallback(() => {
    if (!sessionId) dispatch(setSessionId(uuidv4()));
  }, [dispatch, sessionId]);

  const ensureGuestIdFromCookie = useCallback(() => {
    try {
      const cookieGid = getCookie('guest_id');

      if (cookieGid) {
        if (guestId !== cookieGid) {
          // при любом расхождении — доверяем cookie и синкаем Redux
          dispatch(setGuestId(cookieGid));
        }
        return;
      }

      // куки нет → берём из Redux или генерим и пишем в Redux
      const newId = guestId || uuidv4();
      setCookie('guest_id', newId, { days: 365, sameSite: 'Lax' });
      if (!guestId) dispatch(setGuestId(newId));
    } catch {
      /* no-op */
    }
  }, [dispatch, guestId]);

  const getIsFirstTime = useCallback(
    (pageSlug: string) => {
      const isFirst = firstTimePages[pageSlug] === undefined;
      if (isFirst) dispatch(markPageAsVisited(pageSlug));
      return isFirst;
    },
    [dispatch, firstTimePages],
  );

  // 👇 инициализация сессионной логики + гео
  useEffect(() => {
    ensureGuestIdFromCookie();
    dispatch(initMetrics()); // 👈 поднимает сторожей и проверяет TTL
  }, [dispatch, ensureGuestIdFromCookie]);

  useEffect(() => {
    fetchSessionId(); // твоя старая инициализация (оставил)
    // fetchGeoLocation();
  }, [fetchSessionId]);

  // события
  const isDev = process.env.NODE_ENV !== 'production';

  const trackEvent = async (
    eventName: MetricEventName,
    trigger_source: string,
    additionalParams: Partial<MetricParams> = {},
  ) => {
    if (isDev) return; // не шлём события в dev (по желанию)

    const normalizedName = LEGACY_EVENT_ALIASES[eventName as string] ?? eventName;
    const nowIso = new Date().toISOString();

    const { utm_source: _ignoredUtmSource, utm_content: _ignoredUtmContent, ...restParams } =
      additionalParams;
    const pageUtmFromUrl = readUtmParamsFromSearch(location.search);
    const pageUtm = mergeUtmParams(pageUtmFromUrl, getCurrentPageUtm());

    const params: MetricParams = {
      insert_id: uuidv4(),
      user_id: isLoggedIn && user?.id ? String(user.id) : null,
      guest_id: guestId ?? null,
      is_first_time_on_page: getIsFirstTime(currentPage),
      source: currentPage,
      trigger_source: trigger_source,
      utm_source: pageUtm.utm_source,
      utm_content: pageUtm.utm_content,
      event_date: nowIso,
      session_id: sessionId,
      platform: getPlatformByWidth(),
      os_name: uaResult.os.name || 'unknown',
      device_name: `${uaResult.device.vendor ?? 'unknown'} ${uaResult.device.model ?? ''}`.trim(),
      ...restParams,
    };

    // // 👇 вернул реальные поля region/city (твои строки сверху остаются закомментированы)
    // if (geoLocation?.region) (params as any).region = geoLocation.region;
    // if (geoLocation?.city) (params as any).city = geoLocation.city;

    const cleanedParams = Object.fromEntries(
      Object.entries(params).filter(([, value]) => value != null),
    ) as MetricParams;

    // 👇 перед отправкой продлеваем сессию (TTL) через Redux-операцию
    dispatch(touchSession());

    const backendPayload = {
      insert_id: cleanedParams.insert_id,
      user_id: cleanedParams.user_id ?? undefined,
      guest_id: cleanedParams.guest_id ?? undefined,
      is_first_time_on_page: cleanedParams.is_first_time_on_page,
      event_name: String(normalizedName),
      source: cleanedParams.source,
      trigger_source: cleanedParams.trigger_source,
      utm_source: cleanedParams.utm_source,
      utm_content: cleanedParams.utm_content,
      event_date: cleanedParams.event_date,
      session_id: cleanedParams.session_id,
      platform: cleanedParams.platform,
      os_name: cleanedParams.os_name,
      device_name: cleanedParams.device_name,
      properties: restParams ?? {},
    } as BackendAnalyticsEvent;

    try {
      await dispatch(sendAnalyticsEvent(backendPayload)).unwrap();
      // 2) Если успешно — шлём в Яндекс
      ym('reachGoal', normalizedName, cleanedParams);
    } catch (e) {
      // опционально: console.warn('sendAnalyticsEvent failed', e);
    }
  };

  return { trackEvent, METRIC_EVENTS };
};

export default useMetrics;
